import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DownloadSimple, UploadSimple, ArrowLeft, User
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/config/constants";

export default function AccountPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/export?format=json`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `nature-journal-backup-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} entries`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const { data: result } = await axios.post(`${API}/import`, data);
      toast.success(`Imported ${result.imported} entries (${result.skipped} duplicates skipped)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed — make sure the file is a valid JSON export");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="max-w-xl mx-auto page-content" data-testid="account-page">
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-white shrink-0">
          <ArrowLeft size={20} />
        </Button>
        <h1 className="font-['Cormorant_Garamond'] text-xl sm:text-3xl font-semibold text-white">Account Settings</h1>
      </div>

      {/* Profile Info */}
      <Card className="mb-6 bg-[var(--bg-card)] border-[var(--border-default)]">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)]/10 flex items-center justify-center shrink-0">
              <User size={24} weight="duotone" className="text-[var(--brand-primary)]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-[var(--text-primary)] font-['Manrope'] break-words">{user?.name || "User"}</p>
              <p className="text-xs sm:text-sm text-[var(--text-secondary)] font-['Manrope'] break-all">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Management */}
      <Card className="bg-[var(--bg-card)] border-[var(--border-default)]">
        <CardContent className="p-4 sm:p-6">
          <h2 className="font-['Cormorant_Garamond'] text-lg sm:text-xl font-semibold text-[var(--text-primary)] mb-3 sm:mb-4">Data Management</h2>
          <p className="text-xs sm:text-sm text-[var(--text-secondary)] font-['Manrope'] mb-4">
            Export your journal entries as a backup file, or import a previous backup.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleExport}
              disabled={exporting}
              variant="outline"
              className="flex-1 border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] justify-center"
              data-testid="export-data-btn"
            >
              <DownloadSimple size={18} className="mr-2" />
              {exporting ? "Exporting..." : "Export Data"}
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              variant="outline"
              className="flex-1 border-[var(--border-default)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] justify-center"
              data-testid="import-data-btn"
            >
              <UploadSimple size={18} className="mr-2" />
              {importing ? "Importing..." : "Import Data"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleImportFile}
              className="hidden"
              data-testid="import-file-input"
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
